/**
 * Manages scrolling between intrapage links with consideration
 * for some offset and additional padding.
 */

import Component from '@bythepixel/component-loader';
import scrollToElement from '@Helpers/scrollToElement';

const DEFAULTS = {
  selectors: {
    intraPageLinks: 'a[href^="#"]', // <a href="#foo">Bar</a>
    pageNav: '.header',
  },
};

export default class IntraLinkController extends Component {
  static selector = '[data-comp-intra-link-controller]';

  constructor(containerEl, options = DEFAULTS) {
    super(containerEl);
    this.intraPageLinks = document.querySelectorAll(options.selectors.intraPageLinks);
    this.pageNav = document.querySelector(options.selectors.pageNav);
    this.linkMap = this.buildLinkMap();

    this.mount();
  }

  mount() {
    this.registerListeners();
  }

  registerListeners() {
    this.intraPageLinks.forEach((linkEl) => {
      linkEl.addEventListener('click', (event) => {
        this.handleClick(linkEl, event);
      });
    });
  }

  /**
   * Probably overkill, but beats having to comb the document for the element
   * more than once. Maps an element ID to its respective element.
   *
   * @returns { Map<String, HTMLElement> }
   */
  buildLinkMap() {
    const linkMap = new Map();

    this.intraPageLinks.forEach((linkEl) => {
      const scrollTargetId = this.hrefToElementId(linkEl.href);
      const scrollTarget = document.getElementById(scrollTargetId);

      if (!scrollTarget) {
        console.warn(`IntraLinkController | Element with id '${scrollTargetId}' does not exist.`);
        return;
      }

      linkMap.set(
        scrollTargetId,
        document.getElementById(scrollTargetId),
      );
    });

    return linkMap;
  }

  /**
   * @param { HTMLElement } linkEl
   * @param { Event } event
   *
   * @returns { void }
   */
  handleClick(linkEl, event) {
    event.preventDefault();
    const scrollOffset = this.getNavHeight();
    const scrollTargetId = this.hrefToElementId(linkEl.href);
    const scrollTarget = this.linkMap.get(scrollTargetId);

    scrollToElement(scrollTarget, { offset: scrollOffset });
  }

  /**
   * Extract the URL fragment and remove the hash
   *
   * @param { string } href
   * @returns { string }
   */
  hrefToElementId(href) {
    const { hash } = new URL(href);

    return hash.replace('#', '');
  }

  /**
   * Gets current height of nav to serve as initial offset for the total scroll
   *
   * @returns { number }
   */
  getNavHeight() {
    const { pageNav } = this;
    const navHeight = pageNav.getBoundingClientRect().height;

    return Math.floor(navHeight);
  }
}
